import React from "react";
import {
  Routes as ReactRouterRoutes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { useAuth } from "./state/auth";
import MainLayout from "./layouts/MainLayout";
import AuthSimpleLayout from "./theme/layouts/AuthSimpleLayout";
import CenteredLayout from "./layouts/CenteredLayout";
import MfaSetupRequiredPage from "./pages/MfaSetupRequiredPage";
import LocationPendingPage from "./pages/LocationPendingPage";
import ConfirmLocationPage from "./pages/ConfirmLocationPage";
import { RESTRICTION_LOCATION } from "./utils/mfa";
import PermissionContextCheck from "./components/auth/PermissionContextCheck";
import MeasurementStationsPage from "./pages/MeasurementStationsPage";
import MeasurementStationPage from "./pages/MeasurementStationPage";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import TokensPage from "./pages/TokensPage";
import PasswordRecoveryPage from "./pages/PasswordRecoveryPage";
import UserManagementPage from "./pages/UserManagementPage";
import OrganizationManagementPage from "./pages/OrganizationManagementPage";
import UserEditPage from "./pages/UserEditPage";
import ConversionTablePage from "./pages/ConversionTablePage";
import MeasurementStationFormulasPage from "./pages/MeasurementStationFormulasPage";
import MeasurementStationManualDatapointsPage from "./pages/MeasurementStationManualDatapointsPage";
import AlarmsPage from "./pages/AlarmsPage";
import AlarmLogPage from "./pages/AlarmLogPage";
import MeasurementStationEditPage from "./pages/MeasurementStationEditPage";
import MeasurementStationFilesPage from "./pages/MeasurementStationFilesPage";
import MeasurementStationDataUploadPage from "./pages/MeasurementStationDataUploadPage";
import LabDataUploadPage from "./pages/LabDataUploadPage";
import HMACPage from "./pages/admin/HMACPage";
import UnitEditPage from "./pages/admin/UnitEditPage";
import SensorEditPage from "./pages/admin/SensorEditPage";
import MapLayerEditPage from "./pages/admin/MapLayerEditPage";
import QualityAssurancePage from "./pages/QualityAssurancePage";
import OrganizationMapsPage from "./pages/OrganizationMapsPage";
import OrganizationMapPage from "./pages/OrganizationMapPage";
import StationAliasPage from "./pages/StationAliasPage";
import StationManagementPage from "./pages/StationManagementPage";
import WorldsensingNetworkPage from "./pages/admin/WorldsensingNetworkPage";
import BurrelCamerasPage from "./pages/admin/BurrelCamerasPage";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import StationAuditPage from "./pages/StationAuditPage";
import isEmpty from "lodash/isEmpty";
import OrganizationEditPage from "./pages/OrganizationEditPage";
import OfflineReportsPage from "./pages/OfflineReportsPage";
import ReportDetailPage from "./pages/ReportDetailPage";
import ReportDetailEditPage from "./pages/ReportDetailEditPage";
import DashboardListPage from "./dashboards/pages/DashboardListPage";
import DashboardDetailsPage from "./dashboards/pages/DashboardDetailsPage";
import DashboardAdminAddPage from "./dashboards/pages/DashboardAdminAddPage";
import ReportingDashboardListPage from "./dashboards/pages/ReportingDashboardListPage";
import ReportingDashboardDetailsPage from "./dashboards/pages/ReportingDashboardDetailsPage";
import ReportingDashboardAdminAddPage from "./dashboards/pages/ReportingDashboardAdminAddPage";
import BulkDataExport from "components/export/BulkDataExport";
import { usePersistentState } from "./hooks/state";
import { nativeAppBridge } from "./utils/nativeAppBridge";
import {
  faBell,
  faChartGantt,
  faFileUpload,
  faNetworkWired,
  faMicrophone,
  faMapMarker,
  faClipboardList,
  faFilter,
  faFileLines,
  faCogs,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { StaticTranslationTexts } from "./utils/staticTexts";

export interface RouteLeaf {
  name: string;
  icon?: any;
  active?: boolean;
  exact?: boolean;
  to: string;
  permission?: string;
}

export interface RouteGroup {
  name: string;
  active?: boolean;
  icon: any;
  children: RouteLeaf[];
  permission?: string;
}

export interface RouteCategory {
  label: string;
  labelDisable?: boolean;
  children: (RouteGroup | RouteLeaf)[];
}

export const routes: RouteCategory[] = [
  {
    label: "Map view",
    labelDisable: true,
    children: [
      {
        name: "Map view",
        to: "/stations",
        icon: "earth",
      },
    ],
  },
  {
    label: "Measurement stations",
    labelDisable: false,
    children: [
      {
        name: "show all",
        active: true,
        icon: "chart-line",
        to: "/stations",
      },
    ],
  },
  {
    label: "Dashboards",
    labelDisable: false,
    children: [
      {
        name: "show all",
        active: true,
        icon: "chart-pie",
        to: "/dashboards",
      },
    ],
  },
  {
    label: "Reporting Dashboards",
    labelDisable: false,
    children: [
      {
        name: "show all",
        active: true,
        icon: faFileLines,
        to: "/reporting-dashboards",
      },
    ],
  },
  {
    label: "",
    labelDisable: false,
    children: [
      {
        name: "Manual/lab data",
        active: true,
        icon: faChartGantt,
        to: "/stations/manual-datapoints",
      },
      {
        name: "Alarms",
        active: true,
        icon: faBell,
        to: "/alarms",
        permission: "alarm_admin",
      },
      {
        name: "Reports",
        icon: faClipboardList,
        to: "/admin/reports",
      },
    ],
  },
  {
    label: "Profile",
    children: [
      {
        name: "My profile",
        icon: "user",
        to: "/profile",
      },
    ],
  },
  {
    label: "Organization",
    children: [
      {
        name: "User Management",
        icon: "user-plus",
        to: "/user-management",
        permission: "list_users",
      },
      {
        name: "Organization Management",
        icon: "users",
        to: "/organization-management",
        permission: "edit_organization",
      },
    ],
  },
];
export const adminRoutes: RouteCategory[] = [
  {
    label: "Admin",
    children: [
      {
        name: "HMAC",
        icon: "user-shield",
        to: "/admin/hmac",
      },
      {
        name: "Access tokens",
        icon: "check-circle",
        to: "/tokens",
      },
      {
        name: "Edit Units",
        icon: "percent",
        to: "/admin/units",
      },
      {
        name: "Edit Sensors",
        icon: faMicrophone,
        to: "/admin/sensors",
      },
      {
        name: "Import channel data",
        active: true,
        icon: faFileUpload,
        to: "/admin/stations/data-upload",
      },
      {
        name: "Import XML lab data",
        active: true,
        icon: faFileUpload,
        to: "/admin/xml-lab-data-upload",
      },
      {
        name: "Worldsensing Networks",
        active: true,
        icon: faNetworkWired,
        to: "/admin/worldsensing-networks",
      },
      {
        name: "Burrel Cameras",
        active: true,
        icon: faCamera,
        to: "/admin/burrel-cameras",
      },
      {
        name: "Edit Map Layers",
        icon: faMapMarker,
        to: "/admin/map-layers",
      },
      {
        name: "Quality Assurance",
        icon: faFilter,
        to: "/admin/quality-assurance",
      },
      {
        name: "Organization Maps",
        icon: faMapMarker,
        to: "/admin/organization-maps",
      },
      {
        name: "Station Aliases",
        icon: faMapMarker,
        to: "/admin/station-aliases",
      },
      {
        name: "Station Management",
        icon: faCogs,
        to: "/admin/station-management",
      },
    ],
  },
];

const Routes = () => {
  const { user, loggedIn } = useAuth();
  const isAdmin = user?.admin;
  const isOrganizationMember = !isEmpty(user?.organizationRoles);
  const currentPath = window.location.pathname;
  const apolloClient = useApolloClient();
  const [rememberedPath, setRememberedPath] = usePersistentState(
    "rememberedPath",
    "",
  );

  if (
    !loggedIn &&
    !currentPath.includes("/login") &&
    !currentPath.includes("/confirm-location") &&
    !rememberedPath
  ) {
    setRememberedPath(currentPath);
  }

  const location = useLocation();

  React.useEffect(() => {
    if (!loggedIn || !nativeAppBridge.isNativeConnected) return;
    nativeAppBridge.sendMessage("NAVIGATION_REQUEST", {
      url: window.location.href,
    });
  }, [location, loggedIn]);

  React.useEffect(() => {
    if (!apolloClient) {
      return;
    }
    nativeAppBridge.setApolloClient(apolloClient);
    const cleanup = nativeAppBridge.startListening();
    if (loggedIn) {
      nativeAppBridge.sendMessage("WEBAPP_READY");
    } else {
      nativeAppBridge.sendMessage("WEBAPP_INIT");
    }
    return () => {
      cleanup();
    };
  }, [loggedIn, apolloClient]);

  // A session that may only set up MFA, or wait for the login to be confirmed,
  // sees nothing else: the server refuses everything else anyway
  const restriction = loggedIn ? user?.restriction : null;
  if (restriction) {
    return (
      <ReactRouterRoutes>
        <Route element={<CenteredLayout />}>
          <Route path="/confirm-location" element={<ConfirmLocationPage />} />
          <Route
            path="*"
            element={
              restriction === RESTRICTION_LOCATION ? (
                <LocationPendingPage />
              ) : (
                <MfaSetupRequiredPage />
              )
            }
          />
        </Route>
      </ReactRouterRoutes>
    );
  }

  return (
    <ReactRouterRoutes>
      <Route element={<AuthSimpleLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/authentication/forgot-password"
          element={<PasswordRecoveryPage />}
        />
      </Route>
      {/* Opened from an email, logged in or not */}
      <Route element={<CenteredLayout />}>
        <Route path="/confirm-location" element={<ConfirmLocationPage />} />
      </Route>
      {loggedIn ? (
        <Route element={<MainLayout />}>
          <Route path="/" element={<Navigate to="/stations" />} />
          <Route path="/dashboards" element={<DashboardListPage />} />
          <Route path="/dashboards/:id" element={<DashboardDetailsPage />} />
          <Route
            path="/reporting-dashboards"
            element={<ReportingDashboardListPage />}
          />
          <Route
            path="/reporting-dashboards/:id"
            element={<ReportingDashboardDetailsPage />}
          />
          <Route path="/stations" element={<MeasurementStationsPage />} />
          <Route
            path="/stations/:identifier"
            element={<MeasurementStationPage />}
          />
          <Route path="/maps/:id" element={<OrganizationMapPage />} />
          <Route path="/bulk-export" element={<BulkDataExport />} />
          <Route
            path="/dashboards/admin/add"
            element={<DashboardAdminAddPage />}
          />
          <Route
            path="/reporting-dashboards/admin/add"
            element={<ReportingDashboardAdminAddPage />}
          />
          <Route
            path="/stations/:identifier/formulas/:formulaId"
            element={<MeasurementStationFormulasPage />}
          />
          <Route
            path="/stations/:identifier/formulas"
            element={<MeasurementStationFormulasPage />}
          />
          {isAdmin && (
            <>
              <Route
                path="/stations/:identifier/conversions"
                element={<ConversionTablePage />}
              />
              <Route path="/tokens" element={<TokensPage />} />
              <Route
                path="/stations/:identifier/edit"
                element={<MeasurementStationEditPage />}
              />
              <Route
                path="/admin/stations/data-upload"
                element={<MeasurementStationDataUploadPage />}
              />
              <Route
                path="/admin/xml-lab-data-upload"
                element={<LabDataUploadPage />}
              />
              <Route
                path="/admin/worldsensing-networks"
                element={<WorldsensingNetworkPage />}
              />
              <Route
                path="/admin/burrel-cameras"
              element={<BurrelCamerasPage />}
              />
              <Route path="/admin/hmac" element={<HMACPage />} />
              <Route path="/admin/map-layers" element={<MapLayerEditPage />} />
              <Route
                path="/admin/quality-assurance"
                element={<QualityAssurancePage />}
              />
              <Route
                path="/admin/organization-maps"
                element={<OrganizationMapsPage />}
              />
              <Route
                path="/admin/station-aliases"
                element={<StationAliasPage />}
              />
              <Route
                path="/admin/station-management"
                element={<StationManagementPage />}
              />
            </>
          )}
          <Route
            path="/admin/units"
            element={
              <PermissionContextCheck
                category="unit_edit"
                requiredPermission="read_access"
              >
                <UnitEditPage />
              </PermissionContextCheck>
            }
          />
          <Route
            path="/admin/sensors"
            element={
              <PermissionContextCheck
                category="sensor_edit"
                requiredPermission="read_access"
              >
                <SensorEditPage />
              </PermissionContextCheck>
            }
          />
          <Route path="/admin/reports" element={<OfflineReportsPage />} />
          <Route path="/admin/reports/:uuid" element={<ReportDetailPage />} />
          <Route
            path="/admin/reports/:uuid/edit"
            element={<ReportDetailEditPage />}
          />
          <Route
            path="/stations/manual-datapoints"
            element={<MeasurementStationManualDatapointsPage combined />}
          />
          <Route
            path="/stations/manual-datapoints/:datapointSetId"
            element={<MeasurementStationManualDatapointsPage combined />}
          />
          <Route
            path="/stations/manual-datapoints/:datapointSetId/edit-datapoints"
            element={
              <MeasurementStationManualDatapointsPage editDatapoints combined />
            }
          />
          <Route
            path="/stations/:identifier/manual-datapoints/:datapointSetId"
            element={<MeasurementStationManualDatapointsPage />}
          />
          <Route
            path="/stations/:identifier/manual-datapoints/:datapointSetId/edit-datapoints"
            element={<MeasurementStationManualDatapointsPage editDatapoints />}
          />
          <Route
            path="/stations/:identifier/manual-datapoints"
            element={<MeasurementStationManualDatapointsPage />}
          />
          <Route path="/alarms" element={<AlarmsPage />} />
          <Route path="/alarm-log" element={<AlarmLogPage />} />
          <Route
            path="/user-management"
            element={
              <PermissionContextCheck
                category="user_edit"
                requiredPermission="read_access"
              >
                <UserManagementPage />
              </PermissionContextCheck>
            }
          />
          <Route
            path="/organization-management"
            element={
              <PermissionContextCheck
                category="organization_edit"
                requiredPermission="read_access"
              >
                <OrganizationManagementPage />
              </PermissionContextCheck>
            }
          />
          <Route
            path="/user/:identifier/edit"
            element={
              <PermissionContextCheck
                category="user_edit"
                requiredPermission="read_access"
              >
                <UserEditPage />
              </PermissionContextCheck>
            }
          />

          <Route
            path="/organization/:identifier/edit"
            element={
              <PermissionContextCheck
                category="organization_edit"
                requiredPermission="read_access"
              >
                <OrganizationEditPage />
              </PermissionContextCheck>
            }
          />
          {(isOrganizationMember || isAdmin) && (
            <>
              <Route
                path="/stations/:identifier/files"
                element={<MeasurementStationFilesPage />}
              />
              <Route
                path="/stations/:identifier/audit"
                element={<StationAuditPage />}
              />
              <Route
                path="/dashboards/admin/edit/:id"
                element={<DashboardDetailsPage editWidgets />}
              />
              <Route
                path="/reporting-dashboards/admin/edit/:id"
                element={<ReportingDashboardDetailsPage editWidgets />}
              />
            </>
          )}
          <Route
            path="/stations/:identifier/organization-member-edit/"
            element={<MeasurementStationEditPage />}
          />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="*" element={<p>404</p>} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/login" />} />
      )}
    </ReactRouterRoutes>
  );
};
export default Routes;
